import Helper from '@ember/component/helper';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  DEFAULT_IMG,
  fallbackUrl,
  resolveAvatarUrl,
} from '@linc-technologies/linced-common/utils/avatar-url';

// AvatarUrl resolves a resource's avatar URL for cases where only the URL is
// needed.
//
// Resolution is async, so the helper returns DEFAULT_IMG on first compute and
// re-renders with the real URL once it lands. It shares the blob URL cache with
// <AvatarImage>, so an avatar resolved by either is instant for the other.
export default class AvatarUrl extends Helper {
  @service media;

  @tracked uri = DEFAULT_IMG;

  // last* snapshot what the in-flight resolve was kicked off for. id/mediaId do
  // hang off the resource, but by the time we want to compare, the resource
  // already reports the new values -- the snapshot is what makes a change
  // detectable. It skips redundant resolves on an unrelated recompute, and lets
  // a slow callback recognise its own result as stale so it can't overwrite a
  // newer one.
  lastResource = undefined;
  lastId = undefined;
  lastMediaId = undefined;

  // uriResource records who `uri` was resolved for. A helper instance outlives
  // its argument -- {{each}} and vertical-collection reuse it for a different
  // row -- so without this the previous subject's avatar would render against
  // the new subject's name until the fetch landed.
  uriResource = undefined;

  compute([resource]) {
    // Reading id/mediaId here also entangles them, so the helper recomputes
    // once an unresolved proxy or a not-yet-loaded record fills them in.
    const id = resource?.id ?? null;
    const mediaId = resource?.mediaId ?? null;
    if (
      resource !== this.lastResource ||
      id !== this.lastId ||
      mediaId !== this.lastMediaId
    ) {
      this.lastResource = resource;
      this.lastId = id;
      this.lastMediaId = mediaId;
      this.resolve(resource, mediaId);
    }

    // Read unconditionally: `uri` has to be consumed on every compute or the
    // branch below leaves it unentangled and a later resolve never re-renders.
    const uri = this.uri;

    // Derived rather than reset in place: mutating `uri` here would trip
    // Glimmer's backtracking re-render assertion.
    return resource === this.uriResource ? uri : DEFAULT_IMG;
  }

  resolve(resource, mediaId) {
    // Deferred onto a microtask so `uri` is never mutated during the same
    // render pass that read it, which Glimmer rejects as a backtracking
    // re-render.
    Promise.resolve()
      .then(() => resolveAvatarUrl(this.media, resource))
      .then((url) => {
        if (this.isStale(resource, mediaId)) return;
        // A null url means the record hasn't loaded yet -- hold the current
        // value, the recompute above fires again once it has.
        if (url) this.commit(resource, url);
      })
      .catch((e) => {
        if (this.isStale(resource, mediaId)) return;
        console.error('failed to resolve avatar uri: ' + e);
        this.commit(resource, fallbackUrl(resource?.id));
      });
  }

  // commit publishes a resolved url alongside who it belongs to, so a recompute
  // for a different resource can tell the two apart.
  commit(resource, url) {
    this.uriResource = resource;
    this.uri = url;
  }

  // isStale rejects a result the helper has already moved on from: a different
  // resource, or the same one whose mediaId has since changed.
  isStale(resource, mediaId) {
    return (
      resource !== this.lastResource ||
      mediaId !== this.lastMediaId ||
      this.isDestroying ||
      this.isDestroyed
    );
  }
}
